export type CropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const JPEG_QUALITY = 0.92;

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    if (!src.startsWith('data:')) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = src;
  });

const toJpeg = (canvas: HTMLCanvasElement): string =>
  canvas.toDataURL('image/jpeg', JPEG_QUALITY);

export const clampCrop = (
  crop: CropRect,
  imageWidth: number,
  imageHeight: number,
  minSize = 32
): CropRect => {
  let { x, y, width, height } = crop;
  if (width < 0) {
    x += width;
    width = -width;
  }
  if (height < 0) {
    y += height;
    height = -height;
  }
  const min = Math.min(minSize, imageWidth, imageHeight);
  width = Math.min(Math.max(width, min), imageWidth);
  height = Math.min(Math.max(height, min), imageHeight);
  x = Math.min(Math.max(x, 0), imageWidth - width);
  y = Math.min(Math.max(y, 0), imageHeight - height);
  return { x, y, width, height };
};

export const rotateImageDataUrl = async (
  src: string,
  degrees: 90 | -90
): Promise<string> => {
  const image = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalHeight;
  canvas.height = image.naturalWidth;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas unavailable');
  }
  if (degrees === 90) {
    ctx.translate(canvas.width, 0);
  } else {
    ctx.translate(0, canvas.height);
  }
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(image, 0, 0);
  return toJpeg(canvas);
};

export const cropImageDataUrl = async (
  src: string,
  crop: CropRect
): Promise<string> => {
  const image = await loadImage(src);
  const next = clampCrop(crop, image.naturalWidth, image.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(next.width));
  canvas.height = Math.max(1, Math.round(next.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas unavailable');
  }
  ctx.drawImage(
    image,
    Math.round(next.x),
    Math.round(next.y),
    canvas.width,
    canvas.height,
    0,
    0,
    canvas.width,
    canvas.height
  );
  return toJpeg(canvas);
};
